import { useEffect, useState } from 'react';
import { useValueRef } from './useValueRef';

/**
 * Use to pan around a scrollable area by click/dragging with the mouse.
 * @author @0rvar
 */
export const usePan = (
  onPan: (coords: { left: number; top: number }) => void,
  onlyMiddleMouse: boolean
) => {
  const [elementRef, setRef] = useState<HTMLElement | null>(null);
  const onPanRef = useValueRef(onPan);

  useEffect(() => {
    if (!elementRef) {
      return;
    }
    let cleanup = () => {};
    let lastOffset = { x: 0, y: 0 };
    const onMouseMove = (e: MouseEvent) => {
      const offset = { x: e.clientX, y: e.clientY };
      onPanRef.current({ left: lastOffset.x - offset.x, top: lastOffset.y - offset.y });
      lastOffset = offset;
    };
    const onMouseUp = () => {
      cleanup();
    };
    const setup = (e: MouseEvent) => {
      const isMiddleMouseButton = e.button === 1;
      if (onlyMiddleMouse && !isMiddleMouseButton) {
        return;
      }
      if (e.button > 1) {
        // Ignore higher order mouse buttons
        return;
      }
      e.stopPropagation();
      e.preventDefault();

      lastOffset = { x: e.clientX, y: e.clientY };

      window.addEventListener('mousemove', onMouseMove);
      window.addEventListener('mouseup', onMouseUp);
      cleanup = () => {
        window.removeEventListener('mousemove', onMouseMove);
        window.removeEventListener('mouseup', onMouseUp);
      };
    };
    elementRef.addEventListener('mousedown', setup);
    return () => {
      elementRef.removeEventListener('mousedown', setup);
      cleanup();
    };
  }, [elementRef, onPanRef, onlyMiddleMouse]);

  return setRef;
};
